/**
 * Department report contents.
 *
 * The report has three widening scopes — funcionários, + prestadores, + eventos — and the scope
 * decides which sections exist. Building them is kept here as pure data so the contents can be
 * tested; DepartmentDetail only renders the result into jsPDF/autoTable or CSV.
 */

export type DepartmentReportScope = 'funcionarios' | 'funcionarios_prestadores' | 'completo';

/** Widening order — each scope is the previous one plus a section. */
export const DEPARTMENT_REPORT_SCOPES: DepartmentReportScope[] = [
  'funcionarios',
  'funcionarios_prestadores',
  'completo',
];

export interface DepartmentReportSection {
  title: string;
  head: string[];
  rows: string[][];
  /** Printed in place of the table when there is nothing to list. */
  emptyMessage: string;
}

export interface DepartmentReportMember {
  first_name: string;
  last_name: string;
  position?: string;
  cpf?: string;
  is_active: boolean;
}

export interface DepartmentReportProvider {
  name: string;
  status_today?: string;
  last_entry_at?: string;
}

export interface DepartmentReportEvent {
  person_name?: string;
  person_type?: string;
  event_type?: string;
  direction?: string;
  is_authorized?: boolean;
  status?: string;
  event_timestamp?: string;
  processed_at?: string;
  created_at?: string;
  device_name?: string;
  zone_name?: string;
}

export interface DepartmentReportData {
  members: DepartmentReportMember[];
  serviceProviders: DepartmentReportProvider[];
  events: DepartmentReportEvent[];
}

const DASH = '—';

const orDash = (value?: string | null): string => {
  const v = (value ?? '').toString().trim();
  return v === '' ? DASH : v;
};

const byName = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });

export function departmentReportScopeLabel(scope: DepartmentReportScope): string {
  switch (scope) {
    case 'funcionarios_prestadores':
      return 'Funcionários + Prestadores';
    case 'completo':
      return 'Funcionários + Prestadores + Eventos';
    default:
      return 'Funcionários';
  }
}

/** Human status of one access event, mirroring the wording of the on-screen history. */
function eventLabel(ev: DepartmentReportEvent): string {
  const type = ev.event_type || '';
  const dir = ev.direction || '';
  const ok = ev.is_authorized !== false && ev.status !== 'failure';
  // Explicit denial is checked FIRST. The obvious ordering — authorized-looking direction before
  // the denial types — labels an `access_denied` event that carries `direction: 'entrada'` (and no
  // explicit is_authorized=false) as "Entrada Autorizada", i.e. it reports a refused access as a
  // granted one. The on-screen history in DepartmentDetail had exactly that ordering.
  if (type === 'access_denied' || type === 'authentication_failure' || !ok) return 'Acesso Negado';
  if (type === 'access_granted' || type === 'authentication_success' || (dir === 'entrada' && ok)) {
    return 'Entrada Autorizada';
  }
  if (type.includes('exit') || dir === 'saida') return 'Saída';
  if (dir === 'entrada') return 'Entrada';
  return type.replace(/_/g, ' ') || DASH;
}

/** Portuguese person type. The API answers in either language depending on the producer. */
function personTypeLabel(raw?: string): string {
  switch ((raw || '').toLowerCase()) {
    case 'visitor':
    case 'visitante':
      return 'Visitante';
    case 'service_provider':
    case 'prestador':
      return 'Prestador';
    case 'staff':
    case 'funcionario':
      return 'Funcionário';
    case 'resident':
    case 'morador':
      return 'Morador';
    default:
      return DASH;
  }
}

function eventTimestamp(ev: DepartmentReportEvent): { raw: number; label: string } {
  const raw = ev.event_timestamp || ev.processed_at || ev.created_at;
  if (!raw) return { raw: 0, label: DASH };
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return { raw: 0, label: DASH };
  return {
    raw: date.getTime(),
    label: date.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    }),
  };
}

function membersSection(members: DepartmentReportMember[]): DepartmentReportSection {
  return {
    title: 'Funcionários',
    head: ['Nome', 'Cargo', 'CPF', 'Status'],
    rows: [...members]
      .sort((a, b) => byName(`${a.first_name} ${a.last_name}`, `${b.first_name} ${b.last_name}`))
      .map(m => [
        `${m.first_name} ${m.last_name}`.trim(),
        orDash(m.position),
        orDash(m.cpf),
        m.is_active ? 'Ativo' : 'Inativo',
      ]),
    emptyMessage: 'Nenhum funcionário no departamento',
  };
}

function providersSection(providers: DepartmentReportProvider[]): DepartmentReportSection {
  return {
    title: 'Prestadores',
    head: ['Nome', 'Status hoje', 'Última entrada'],
    rows: [...providers]
      .sort((a, b) => byName(a.name || '', b.name || ''))
      .map(p => {
        const last = p.last_entry_at ? new Date(p.last_entry_at) : null;
        return [
          orDash(p.name),
          orDash(p.status_today),
          last && !Number.isNaN(last.getTime())
            ? last.toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
              })
            : DASH,
        ];
      }),
    emptyMessage: 'Nenhum prestador vinculado ao departamento',
  };
}

function eventsSection(events: DepartmentReportEvent[]): DepartmentReportSection {
  return {
    title: 'Eventos de acesso',
    head: ['Data/Hora', 'Pessoa', 'Tipo', 'Evento', 'Local'],
    rows: [...events]
      // Newest first, matching the on-screen history. The caller's list is usually already
      // sorted, but the report must not depend on that.
      .sort((a, b) => eventTimestamp(b).raw - eventTimestamp(a).raw)
      .map(ev => [
        eventTimestamp(ev).label,
        orDash(ev.person_name),
        personTypeLabel(ev.person_type),
        eventLabel(ev),
        orDash(ev.device_name || ev.zone_name),
      ]),
    emptyMessage: 'Nenhum evento de acesso no período',
  };
}

export function buildDepartmentReportSections(
  scope: DepartmentReportScope,
  data: DepartmentReportData,
): DepartmentReportSection[] {
  const sections = [membersSection(data.members)];
  if (scope === 'funcionarios_prestadores' || scope === 'completo') {
    sections.push(providersSection(data.serviceProviders));
  }
  if (scope === 'completo') {
    sections.push(eventsSection(data.events));
  }
  return sections;
}

const csvCell = (value: string): string => `"${String(value).replace(/"/g, '""')}"`;

export function buildDepartmentReportCsv(
  departmentName: string,
  sections: DepartmentReportSection[],
): string {
  const lines: string[] = [csvCell(`Departamento: ${departmentName}`)];
  sections.forEach(section => {
    lines.push('');
    lines.push(csvCell(section.title));
    if (section.rows.length === 0) {
      // Written out rather than skipped: a missing block reads as "not included in this report"
      // instead of "nothing to report".
      lines.push(csvCell(section.emptyMessage));
      return;
    }
    lines.push(section.head.map(csvCell).join(','));
    section.rows.forEach(row => lines.push(row.map(csvCell).join(',')));
  });
  return lines.join('\n');
}

export function departmentReportFileName(
  departmentName: string,
  scope: DepartmentReportScope,
  extension: string,
  now: Date = new Date(),
): string {
  const safeName = departmentName.replace(/[\s/\\]+/g, '_');
  return `dept_${safeName}_${scope}_${now.toISOString().slice(0, 10)}.${extension}`;
}
